'use client';

import { useCookies } from 'react-cookie';
import { isFilled } from '@prismicio/client';
import { createContext, useContext, useState } from 'react';

import {
  SearchParamName,
  retainableQueryParams,
  useRetainableSearchParamsContext,
} from '@preventx/prismic-services';
import { RadioGroupState } from '@preventx/oxygen';

// Types
import {
  Context,
  ProviderProps,
  OnNavigateRequest,
} from '@/types/contexts/productDetailsContext';

// Contexts
import { applicationRerouteUrl } from '@/services/site/navigation/shared';

export const ProductDetailsContext = createContext<Context>({
  onNavigate: () => [],

  setProduct: () => [],
  product: { isValid: false, radioValue: '' },
});

export const ProductDetailsProvider = ({ children }: ProviderProps) => {
  const { searchParams } = useRetainableSearchParamsContext();

  const [_cookies, _setCookies, removeCookie] = useCookies(
    retainableQueryParams,
  );

  const [product, setProduct] = useState<RadioGroupState>({
    isValid: false,
    radioValue: '',
  });

  const onNavigate = ({ productKey, application }: OnNavigateRequest) => {
    if (!productKey || application === 'Default') {
      return;
    }

    let params: Record<SearchParamName | string, string> = {
      productKey: productKey,
    };

    searchParams.forEach(({ key, value }) => {
      if (!value) {
        return;
      }

      params[key] = value;
    });

    const field = applicationRerouteUrl({
      params: params,
      application: application,
    });

    if (!isFilled.link(field) || !field.url) {
      return;
    }

    removeCookie('ref');

    location.href = field.url;
  };

  return (
    <ProductDetailsContext.Provider
      value={{
        onNavigate: onNavigate,

        product: product,
        setProduct: setProduct,
      }}
    >
      {children}
    </ProductDetailsContext.Provider>
  );
};

export const useProductDetailsContext = () =>
  useContext<Context>(ProductDetailsContext);
